import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import SnipedOddsCard from './snipedOddsCard';
import SnipedOddsSettingsModal from './snipedOddsSettingsModal';
import AdCard from '../betFeed/adCard';
import RegistrationSuggestionCard from '../betFeed/registrationSuggestionCard';
import RegistrationModal from '../betFeed/registrationModal';
import { debounce } from 'lodash';
import globalStyles from '../globals.module.css';
import componentStyles from './snipedOdds.module.css';
import { getAllSnipedOdds, getAvailableProps } from '../../service/snipedOddsService';
import SessionService from '../../service/sessionService';
import AccessService from '../../service/accessService';
import BetCartService from '../../service/betCartService';
import eventBus from '../../service/eventBus';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import AnalyticsService from '../../service/analyticsService';
import { EVENT_SNIPED_TAB_CHANGED, EVENT_SNIPED_VIEW_MORE, EVENT_REGISTRATION_MODAL_SHOWN } from '../../utils/analyticsConstants';
import { AD_INJECTION_INTERVAL, EVENT_BET_CART_UPDATED, LOGGED_OUT_VISIBLE_CARD_LIMIT } from '../../utils/constants';
import { FaTimes, FaFilter, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import PageView from '../common/pageView';

const normalizeAvailableSports = (sports = []) => (
    Array.isArray(sports)
        ? sports
            .map((sport) => {
                const counts = sport?.counts || {};
                return {
                    sportType: String(sport?.sport_type || sport?.sportType || sport || '').trim().toUpperCase(),
                    count: sport?.count ?? sport?.total ?? null,
                    counts: {
                        value: counts.value ?? counts.price ?? sport?.valueCount ?? sport?.priceCount ?? sport?.value_count ?? sport?.price_count ?? null,
                        over: counts.over ?? counts.overs ?? sport?.overCount ?? sport?.oversCount ?? sport?.over_count ?? sport?.overs_count ?? null,
                        under: counts.under ?? counts.unders ?? sport?.underCount ?? sport?.undersCount ?? sport?.under_count ?? sport?.unders_count ?? null
                    }
                };
            })
            .filter((sport) => sport.sportType)
        : []
);

const getSportCountForTab = (sport = {}, tab = 'price') => {
    if (tab === 'overs') {
        return sport.counts?.over ?? sport.count;
    }

    if (tab === 'unders') {
        return sport.counts?.under ?? sport.count;
    }

    return sport.counts?.value ?? sport.count;
};

const getSnipedEventName = (snipedOdd = {}) => {
    const awayTeam = snipedOdd.away_team || snipedOdd.awayTeam || '';
    const homeTeam = snipedOdd.home_team || snipedOdd.homeTeam || '';

    if (awayTeam && homeTeam) return `${awayTeam} @ ${homeTeam}`;
    return snipedOdd.event_name || snipedOdd.eventName || 'Event TBD';
};

const getSnipedMarketDisplayName = (snipedOdd = {}) => {
    const displayName = snipedOdd.display?.prop_display_name || snipedOdd.market_display_name || snipedOdd.marketDisplayName;
    const marketKey = snipedOdd.market_key || snipedOdd.marketKey || '';

    if (displayName) return displayName;
    return marketKey
        ? marketKey.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        : 'Prop';
};

const buildCartBetFromSnipedOdd = (snipedOdd = {}) => {
    const documentId = snipedOdd.statspyder_document_id || snipedOdd.statspyderDocumentId || snipedOdd._id || '';
    const display = snipedOdd.display || {};
    const best = snipedOdd.best || {};
    const marketType = snipedOdd.market_key || snipedOdd.marketKey || best.market_key || '';
    const marketDisplayName = getSnipedMarketDisplayName(snipedOdd);
    const direction = snipedOdd.direction || best.direction || '';
    const line = display.line ?? best.point ?? snipedOdd.other_point ?? null;
    const player = snipedOdd.player_name || snipedOdd.playerName || '';
    const event = getSnipedEventName(snipedOdd);
    const recommendedBet = [direction, line, marketDisplayName].filter((item) => item !== null && item !== undefined && item !== '').join(' ');

    return {
        source: 'sniped_odds',
        sourceBetId: documentId,
        cartKey: documentId || `${player}-${snipedOdd.event_id || snipedOdd.eventId || ''}-${marketType}-${direction}-${line}`,
        statspyderDocumentId: documentId,
        type: 'Prop',
        betType: 'player_prop',
        sport: snipedOdd.sport_type || snipedOdd.sportType || snipedOdd.sport || '',
        event,
        eventName: event,
        eventId: snipedOdd.event_id || snipedOdd.eventId || '',
        oddsApiEventId: snipedOdd.event_id || snipedOdd.eventId || '',
        date: snipedOdd.commence_time || snipedOdd.commenceTime || snipedOdd.date_time || snipedOdd.dateTime || '',
        bet: recommendedBet || 'Sniped odds prop',
        label: recommendedBet || 'Sniped odds prop',
        recommendedBet: recommendedBet || 'Sniped odds prop',
        player,
        marketType,
        marketDisplayName,
        direction,
        line,
        bookmaker: display.bookmaker || best.bookmaker || snipedOdd.other_bookmaker || '',
        odds: display.odds ?? best.odds ?? snipedOdd.other_odds ?? null,
        sourcePayload: {
            statspyder_document_id: documentId,
            event_id: snipedOdd.event_id || snipedOdd.eventId || '',
            sport_type: snipedOdd.sport_type || snipedOdd.sportType || snipedOdd.sport || '',
            player_name: player,
            market_key: marketType,
            direction,
            best
        }
    };
};

const SnipedOdds = () => {
    const componentId = 'SnipedOdds';
    // Get session service instance first
    const sessionService = SessionService.getInstance();
    const accessService = AccessService.getInstance();
    const betCartService = useMemo(() => BetCartService.getInstance(), []);
    const canUseSnipedOddsFilters = accessService.isProOrAdmin();
    const viewState = sessionService.getViewState('snipedOdds');

    const [loading, setLoading] = useState(false);
    const feedRef = useRef(null);
    const [pageSize] = useState(25);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isAuthenticated, setIsAuthenticated] = useState(sessionService.isAuthenticated());
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [hasShownScrollSignupPrompt, setHasShownScrollSignupPrompt] = useState(false);
    const firstLockedCardRef = useRef(null);
    
    // Tab-specific states - each tab has its own data and pagination.
    // Best Overs is the visible default. Migrate the retired Best Value selection.
    const [activeTab, setActiveTab] = useState(() => {
        const saved = sessionService.getLocalSetting('snipedOdds_activeTab', 'overs');
        return !saved || saved === 'price' ? 'overs' : saved;
    }); // 'overs', 'unders', or null
    
    // Price tab (Best Value) state
    const [priceOdds, setPriceOdds] = useState([]);
    const [pricePage, setPricePage] = useState(1);
    // Total rows available for the current filters (from API `data.total`)
    const [priceCount, setPriceCount] = useState(0);
    // Rows returned in the most recent page response (from API `data.count`)
    const [priceLastPageCount, setPriceLastPageCount] = useState(0);
    const [priceHasMore, setPriceHasMore] = useState(true);
    const [priceScrollPosition, setPriceScrollPosition] = useState(0);
    
    // Overs tab (Best Overs) state
    const [overOdds, setOverOdds] = useState([]);
    const [overPage, setOverPage] = useState(1);
    // Total rows available for the current filters (from API `data.total`)
    const [overCount, setOverCount] = useState(0);
    // Rows returned in the most recent page response (from API `data.count`)
    const [overLastPageCount, setOverLastPageCount] = useState(0);
    const [overHasMore, setOverHasMore] = useState(true);
    const [overScrollPosition, setOverScrollPosition] = useState(0);
    
    // Unders tab (Best Unders) state
    const [underOdds, setUnderOdds] = useState([]);
    const [underPage, setUnderPage] = useState(1);
    // Total rows available for the current filters (from API `data.total`)
    const [underCount, setUnderCount] = useState(0);
    // Rows returned in the most recent page response (from API `data.count`)
    const [underLastPageCount, setUnderLastPageCount] = useState(0);
    const [underHasMore, setUnderHasMore] = useState(true);
    const [underScrollPosition, setUnderScrollPosition] = useState(0);
    
    // Sport and prop selection - single select only
    // Start with null - no auto-restore from localStorage on initial load
    const [selectedSport, setSelectedSport] = useState(null);
    const [selectedPropType, setSelectedPropType] = useState(null);
    
    const [availableProps, setAvailableProps] = useState([]);
    const [availableSports, setAvailableSports] = useState([]);
    const [savedBetIds, setSavedBetIds] = useState(() => new Set(
        betCartService.getAllBets().map((bet) => bet.id)
    ));

    // Expanded cards state - track which cards are showing all opportunities
    const [expandedCardIds, setExpandedCardIds] = useState(new Set());
    const [loadingAllOpportunities, setLoadingAllOpportunities] = useState(false);
    
    // Filter modal state
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [draftActiveTab, setDraftActiveTab] = useState(activeTab);
    const [draftSelectedSport, setDraftSelectedSport] = useState(selectedSport);
    const [draftSelectedPropType, setDraftSelectedPropType] = useState(selectedPropType);
    const [draftAvailableProps, setDraftAvailableProps] = useState([]);
    const [loadingDraftProps, setLoadingDraftProps] = useState(false);
    
    const supportedSports = useMemo(() => {
        if (availableSports.length > 0) {
            return availableSports
                .map((sport) => ({
                    ...sport,
                    count: getSportCountForTab(sport, activeTab)
                }))
                .filter((sport) => sport.count === null || sport.count === undefined || Number(sport.count) > 0);
        }

        const userSports = sessionService.getFilteredActiveSports();
        const sports = Array.isArray(userSports) && userSports.length > 0
            ? userSports
            : ['nba', 'nfl', 'mlb', 'wnba'];

        return [...new Set(
            sports
                .map((sport) => String(sport || '').trim().toUpperCase())
                .filter(Boolean)
        )].map((sportType) => ({
            sportType,
            count: null
        }));
    }, [activeTab, availableSports, sessionService]);
    
    // Helper function to get display name for selected prop
    const getSelectedPropDisplayName = () => {
        if (!selectedPropType) return null;
        const prop = availableProps.find(p => getPropKey(p) === selectedPropType);
        return prop ? getPropLabel(prop) : selectedPropType;
    };

    const getDraftPropDisplayName = () => {
        if (!draftSelectedPropType) return null;
        const prop = draftAvailableProps.find(p => getPropKey(p) === draftSelectedPropType);
        return prop ? getPropLabel(prop) : draftSelectedPropType;
    };

    const getPropKey = (prop) => prop?.market_key || prop?.marketKey || prop?.type || prop?.key || '';

    const getPropLabel = (prop) => (
        prop?.display_name ||
        prop?.displayName ||
        prop?.label ||
        prop?.name ||
        getPropKey(prop)
    );

    const getPropCount = (prop, tab = activeTab) => {
        if (tab === 'overs') {
            return prop?.counts?.over ?? prop?.count ?? prop?.prop_count ?? null;
        }

        if (tab === 'unders') {
            return prop?.counts?.under ?? prop?.count ?? prop?.prop_count ?? null;
        }

        return prop?.counts?.value ?? prop?.count ?? prop?.prop_count ?? null;
    };

    const isPropAvailable = (prop, tab = activeTab) => {
        const count = getPropCount(prop, tab);
        return count === null || count === undefined || Number(count) > 0;
    };
    
    const getCurrentViewLabel = (tab = activeTab) => {
        switch (tab) {
            case 'overs':
                return 'Best Overs';
            case 'unders':
                return 'Best Unders';
            case 'price':
                return 'Best Value';
            default:
                return 'No View';
        }
    };

    const activeFilterCount = (
        (activeTab && activeTab !== 'overs' ? 1 : 0) +
        (selectedSport ? 1 : 0) +
        (selectedPropType ? 1 : 0)
    );

    const draftFilterCount = (
        (draftActiveTab && draftActiveTab !== 'overs' ? 1 : 0) +
        (draftSelectedSport ? 1 : 0) +
        (draftSelectedPropType ? 1 : 0)
    );

    const visibleFilterPills = [
        { key: 'view', label: getCurrentViewLabel() },
        { key: 'sport', label: selectedSport || 'All Sports' },
        { key: 'prop', label: selectedPropType ? getSelectedPropDisplayName() : 'All Props' }
    ].filter((pill) => pill.label);

    const openFilterModal = () => {
        setDraftActiveTab(activeTab);
        setDraftSelectedSport(selectedSport);
        setDraftSelectedPropType(selectedPropType);
        setDraftAvailableProps(availableProps);
        setShowFilterModal(true);
    };

    const closeFilterModal = () => {
        setShowFilterModal(false);
    };

    const clearDraftFilters = () => {
        setDraftActiveTab('overs');
        setDraftSelectedSport(null);
        setDraftSelectedPropType(null);
    };

    const applyDraftFilters = () => {
        const nextActiveTab = draftActiveTab || 'overs';
        const filtersChanged = (
            nextActiveTab !== activeTab ||
            draftSelectedSport !== selectedSport ||
            draftSelectedPropType !== selectedPropType
        );

        if (nextActiveTab !== activeTab) {
            AnalyticsService.getInstance().track(EVENT_SNIPED_TAB_CHANGED, { tab: nextActiveTab });
        }

        setActiveTab(nextActiveTab);
        setSelectedSport(draftSelectedSport);
        setSelectedPropType(draftSelectedSport ? draftSelectedPropType : null);

        if (filtersChanged) {
            resetAllTabData();
            setExpandedCardIds(new Set());
            setTimeout(() => {
                if (feedRef.current) {
                    feedRef.current.scrollTop = 0;
                }
            }, 100);
        }

        setShowFilterModal(false);
    };

    const handleDraftSportSelect = (sport) => {
        if (sport === draftSelectedSport) {
            setDraftSelectedSport(null);
        } else {
            setDraftSelectedSport(sport);
        }
        setDraftSelectedPropType(null);
    };

    const handleDraftPropSelect = (propType) => {
        if (propType === draftSelectedPropType) {
            setDraftSelectedPropType(null);
        } else {
            setDraftSelectedPropType(propType);
        }
    };

    // Reset all tab lists/paging when any filter changes (Phase 4 wiring)
    const resetAllTabData = () => {
        setPriceOdds([]);
        setPricePage(1);
        setPriceCount(0);
        setPriceLastPageCount(0);
        setPriceHasMore(true);
        setPriceScrollPosition(0);
        setOverOdds([]);
        setOverPage(1);
        setOverCount(0);
        setOverLastPageCount(0);
        setOverHasMore(true);
        setOverScrollPosition(0);
        setUnderOdds([]);
        setUnderPage(1);
        setUnderCount(0);
        setUnderLastPageCount(0);
        setUnderHasMore(true);
        setUnderScrollPosition(0);
        setScrollPosition(0);
    };

    useEffect(() => {
        if (!selectedSport || availableSports.length === 0) return;

        const selectedSportStillAvailable = supportedSports.some((sport) => sport.sportType === selectedSport);
        if (!selectedSportStillAvailable) {
            setSelectedSport(null);
            setSelectedPropType(null);
            resetAllTabData();
        }
    }, [availableSports.length, selectedSport, supportedSports]);
    
    // Save selections to local settings
    useEffect(() => {
        sessionService.setLocalSetting('snipedOdds_selectedSport', selectedSport);
    }, [selectedSport, sessionService]);
    
    useEffect(() => {
        sessionService.setLocalSetting('snipedOdds_selectedPropType', selectedPropType);
    }, [selectedPropType, sessionService]);

    useEffect(() => {
        const updateSavedBetIds = () => {
            setSavedBetIds(new Set(betCartService.getAllBets().map((bet) => bet.id)));
        };

        eventBus.on('SnipedOdds', EVENT_BET_CART_UPDATED, updateSavedBetIds);
        return () => eventBus.off('SnipedOdds', EVENT_BET_CART_UPDATED);
    }, [betCartService]);

    // Fetch available props when sport changes
    useEffect(() => {
        if (selectedSport && isAuthenticated && canUseSnipedOddsFilters) {
            const fetchProps = async () => {
                try {
                    const response = await getAvailableProps(selectedSport);
                    // Response structure: { success: true, results: { sport: "NFL", props: [...] }, error: null }
                    if (response?.data?.success && response?.data?.results?.props) {
                        setAvailableProps(response.data.results.props);
                    } else {
                        setAvailableProps([]);
                    }
                } catch (error) {
                    console.error('Error fetching available props:', error);
                    setAvailableProps([]);
                }
            };
            
            fetchProps();
        } else {
            setAvailableProps([]);
            setSelectedPropType(null); // Clear prop when sport is cleared
        }
    }, [selectedSport, isAuthenticated, canUseSnipedOddsFilters]);

    useEffect(() => {
        if (!showFilterModal || !draftSelectedSport || !isAuthenticated || !canUseSnipedOddsFilters) {
            setDraftAvailableProps([]);
            setDraftSelectedPropType(null);
            setLoadingDraftProps(false);
            return;
        }

        if (draftSelectedSport === selectedSport) {
            setDraftAvailableProps(availableProps);
            setLoadingDraftProps(false);
            return;
        }

        let isCancelled = false;
        const fetchDraftProps = async () => {
            setLoadingDraftProps(true);
            try {
                const response = await getAvailableProps(draftSelectedSport);
                const props = response?.data?.success && response?.data?.results?.props
                    ? response.data.results.props
                    : [];
                if (!isCancelled) {
                    setDraftAvailableProps(props);
                }
            } catch (error) {
                console.error('Error fetching draft available props:', error);
                if (!isCancelled) {
                    setDraftAvailableProps([]);
                }
            } finally {
                if (!isCancelled) {
                    setLoadingDraftProps(false);
                }
            }
        };

        fetchDraftProps();

        return () => {
            isCancelled = true;
        };
    }, [showFilterModal, draftSelectedSport, selectedSport, availableProps, isAuthenticated, canUseSnipedOddsFilters]);
    
    // Note: prop selection is an optional filter; clearing it should not clear the active tab.
    
    useEffect(() => {
        sessionService.setLocalSetting('snipedOdds_activeTab', activeTab);
    }, [activeTab, sessionService]);
    
    // Create a debounced version of saveViewState
    const debouncedSaveViewState = useCallback(
        debounce(() => {
            // Save view state for restoration - tab-specific
            const currentData = getCurrentTabData();
            SessionService.getInstance().setViewState('snipedOdds', {
                'activeTab': activeTab,
                'pricePage': pricePage,
                'priceScrollPosition': priceScrollPosition,
                'overPage': overPage,
                'overScrollPosition': overScrollPosition,
                'underPage': underPage,
                'underScrollPosition': underScrollPosition,
                'selectedSport': selectedSport,
                'selectedPropType': selectedPropType,
                'scrollPosition': scrollPosition
            });
        }, 2000),
        [
            activeTab,
            pricePage,
            priceScrollPosition,
            overPage,
            overScrollPosition,
            underPage,
            underScrollPosition,
            selectedSport,
            selectedPropType,
            scrollPosition
        ]
    );

    // Helper to get current tab data
    const getCurrentTabData = () => {
        switch (activeTab) {
            case 'price':
                return { odds: priceOdds, page: pricePage, scrollPos: priceScrollPosition };
            case 'overs':
                return { odds: overOdds, page: overPage, scrollPos: overScrollPosition };
            case 'unders':
                return { odds: underOdds, page: underPage, scrollPos: underScrollPosition };
            default:
                return { odds: [], page: 1, scrollPos: 0 };
        }
    };


    // Load more odds on scroll - tab-specific
    const loadMoreOdds = useCallback(() => {
        // Stop conditions:
        // - Don't load while already loading
        // - Don't load if we've already loaded all items for this tab (items.length >= total)
        // - Don't load if the most recent page returned 0 items
        if (loading || !activeTab || !isAuthenticated) return;

        let currentListLength = 0;
        let totalAvailable = 0;
        let lastPageCount = 0;
        let hasMore = true;

        switch (activeTab) {
            case 'price':
                currentListLength = priceOdds.length;
                totalAvailable = priceCount;
                lastPageCount = priceLastPageCount;
                hasMore = priceHasMore;
                break;
            case 'overs':
                currentListLength = overOdds.length;
                totalAvailable = overCount;
                lastPageCount = overLastPageCount;
                hasMore = overHasMore;
                break;
            case 'unders':
                currentListLength = underOdds.length;
                totalAvailable = underCount;
                lastPageCount = underLastPageCount;
                hasMore = underHasMore;
                break;
        }

        if (hasMore === false) return;
        if (totalAvailable <= 0) return;
        if (currentListLength >= totalAvailable) return;
        if (lastPageCount === 0) return;

        switch (activeTab) {
            case 'price':
                setPricePage((prev) => prev + 1);
                break;
            case 'overs':
                setOverPage((prev) => prev + 1);
                break;
            case 'unders':
                setUnderPage((prev) => prev + 1);
                break;
        }
    }, [
        loading,
        activeTab,
        priceOdds.length,
        priceCount,
        priceLastPageCount,
        priceHasMore,
        overOdds.length,
        overCount,
        overLastPageCount,
        overHasMore,
        underOdds.length,
        underCount,
        underLastPageCount,
        underHasMore,
        isAuthenticated
    ]);

    // Handle scroll event for infinite scroll
    const handleScroll = useCallback(() => {
        if (!feedRef.current) return;
        
        const { scrollTop, scrollHeight, clientHeight } = feedRef.current;
        setScrollPosition(scrollTop);
        
        // Save scroll position for current tab
        switch (activeTab) {
            case 'price':
                setPriceScrollPosition(scrollTop);
                break;
            case 'overs':
                setOverScrollPosition(scrollTop);
                break;
            case 'unders':
                setUnderScrollPosition(scrollTop);
                break;
        }
        
        // Infinite scroll
        // Note: API auth is cookie-based; don't block paging on SessionService auth flags.
        if (!loading) {
            const shouldLoadMore = scrollTop + clientHeight >= scrollHeight - 5;
            if (shouldLoadMore) {
                loadMoreOdds();
            }
        }

        debouncedSaveViewState();
    }, [activeTab, loading, loadMoreOdds, debouncedSaveViewState]);

    // Fetch sniped odds from API - tab-specific with type parameter
    const fetchSnipedOdds = useCallback(async (includeAll = false) => {
        // Don't fetch if tab isn't selected.
        // Note: API auth is cookie-based; we rely on the request itself to succeed/fail.
        if (!activeTab) {
            setLoading(false);
            return;
        }
        
        // Set appropriate loading state
        if (includeAll) {
            setLoadingAllOpportunities(true);
        } else {
        setLoading(true);
        }
        
        // Map activeTab to API type parameter
        const typeMap = {
            'price': 'price',
            'overs': 'overs',
            'unders': 'unders'
        };
        const apiType = typeMap[activeTab];
        if (!apiType) {
            setLoading(false);
            setLoadingAllOpportunities(false);
            return;
        }
        
        // Get current tab's page number
        let currentPage;
        let setOdds, setPage, setCount, setLastPageCount, setHasMore;
        switch (activeTab) {
            case 'price':
                currentPage = pricePage;
                setOdds = setPriceOdds;
                setPage = setPricePage;
                setCount = setPriceCount;
                setLastPageCount = setPriceLastPageCount;
                setHasMore = setPriceHasMore;
                break;
            case 'overs':
                currentPage = overPage;
                setOdds = setOverOdds;
                setPage = setOverPage;
                setCount = setOverCount;
                setLastPageCount = setOverLastPageCount;
                setHasMore = setOverHasMore;
                break;
            case 'unders':
                currentPage = underPage;
                setOdds = setUnderOdds;
                setPage = setUnderPage;
                setCount = setUnderCount;
                setLastPageCount = setUnderLastPageCount;
                setHasMore = setUnderHasMore;
                break;
            default:
                setLoading(false);
                setLoadingAllOpportunities(false);
                return;
        }
        
        // Note: view-state restore is intentionally not used to short-circuit fetching.
        // Phase 2 resets clear items on tab/filter changes, so skipping fetch here could leave the list empty.

        // Build request object with type + pagination + filters (new API contract).
        // Backend expects page/pageSize; limit is only a fallback if pageSize is omitted.
        const request = {
            type: apiType,
            page: includeAll ? 1 : currentPage,
            pageSize: pageSize,
            includeAllOpportunities: includeAll
        };

        // Add filters (optional).
        // Sport is visible in the simplified UI; the rest stay behind the hidden advanced filter flag.
        if (canUseSnipedOddsFilters && selectedSport) request.sportType = selectedSport;
        if (canUseSnipedOddsFilters && selectedPropType) request.propTypes = [selectedPropType];

        try {
            const response = await getAllSnipedOdds(request);
            const { data } = response;

            // New response shape:
            // { success: true, data: { total, count, results, page, page_size, ... } }
            const {success, results} = data;
            if (success && results) {
                const payload = results;
                const newOdds = payload.results || [];
                const total = payload.total || 0;
                const pageCount = payload.count || 0;
                const hasMore = payload.pagination?.hasMore;
                const nextAvailableSports = normalizeAvailableSports(payload.available_sports);

                if (nextAvailableSports.length > 0) {
                    setAvailableSports(nextAvailableSports);
                }

                // Track total available rows for the current tab (used for labels + paging logic later).
                setCount(total);
                // Track last page size so infinite scroll can stop when backend returns 0 rows.
                setLastPageCount(pageCount);
                if (typeof hasMore === 'boolean') {
                    setHasMore(hasMore);
                }
                
                if (currentPage === 1 || includeAll) {
                    const oddsWithIds = newOdds.map((odd, index) => ({
                        ...odd,
                        indexId: `${activeTab}_${index + 1}`
                    }));
                    setOdds(oddsWithIds);
                } else {
                    setOdds((prevOdds) => {
                        const oddsWithIds = newOdds.map((odd, index) => ({
                            ...odd,
                            indexId: `${activeTab}_${prevOdds.length + index + 1}`
                        }));
                        return [...prevOdds, ...oddsWithIds];
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching sniped odds:', error);
        } finally {
            setTimeout(() => {
                setLoading(false);
                setLoadingAllOpportunities(false);
            }, 300);
        }
    }, [
        activeTab,
        pricePage,
        overPage,
        underPage,
        selectedSport,
        selectedPropType,
        pageSize,
        isAuthenticated,
        canUseSnipedOddsFilters
    ]);
    
    // Get current list based on active tab
    const getCurrentList = () => {
        switch (activeTab) {
            case 'price':
                return priceOdds;
            case 'overs':
                return overOdds;
            case 'unders':
                return underOdds;
            default:
                return [];
        }
    };

    const injectPromotionalCards = (cards) => {
        if (isAuthenticated) {
            return cards;
        }

        const cardsWithPromotional = [];
        let promoInsertions = 0;

        cards.forEach((card, index) => {
            cardsWithPromotional.push(card);
            if ((index + 1) % AD_INJECTION_INTERVAL === 0) {
                const isAdSlot = promoInsertions % 2 === 0;
                cardsWithPromotional.push({
                    type: isAdSlot ? 'ad' : 'registration',
                    indexId: isAdSlot ? `sniped-ad-card-${index}` : `sniped-registration-suggestion-${index}`
                });
                promoInsertions++;
            }
        });

        return cardsWithPromotional;
    };

    const shouldBlurCard = (items, card, index) => {
        if (isAuthenticated) return false;
        if (card.type === 'ad' || card.type === 'registration') return false;

        let visibleCount = 0;
        for (let i = 0; i <= index; i++) {
            const currentCard = items[i];
            if (currentCard.type !== 'ad' && currentCard.type !== 'registration') {
                visibleCount++;
            }
        }

        return visibleCount > LOGGED_OUT_VISIBLE_CARD_LIMIT;
    };

    const isFirstLockedCard = (items, card, index) => {
        if (!shouldBlurCard(items, card, index)) return false;

        let visibleCount = 0;
        for (let i = 0; i <= index; i++) {
            const currentCard = items[i];
            if (currentCard.type !== 'ad' && currentCard.type !== 'registration') {
                visibleCount++;
            }
        }

        return visibleCount === LOGGED_OUT_VISIBLE_CARD_LIMIT + 1;
    };
    
    // Handle "View More" click - fetch all opportunities and expand card
    const handleViewMore = useCallback(async (cardId) => {
        if (!canUseSnipedOddsFilters) {
            if (isAuthenticated) {
                setShowUpgradeModal(true);
            } else {
                AnalyticsService.getInstance().track(EVENT_REGISTRATION_MODAL_SHOWN, { trigger: 'sniped_view_more' });
                setShowRegistrationModal(true);
            }
            return;
        }

        AnalyticsService.getInstance().track(EVENT_SNIPED_VIEW_MORE, { cardId: String(cardId ?? ''), tab: activeTab ?? '' });
        // Check if we already have all_opportunities data for any card
        const currentList = getCurrentList();
        const hasAllOpportunities = currentList.some(odd => odd.all_opportunities && odd.all_opportunities.length > 0);
        
        // If we don't have all_opportunities data, fetch it
        if (!hasAllOpportunities) {
            await fetchSnipedOdds(true);
        }
        
        // Expand the card
        setExpandedCardIds((prev) => {
            const newSet = new Set(prev);
            newSet.add(cardId);
            return newSet;
        });
    }, [canUseSnipedOddsFilters, isAuthenticated, fetchSnipedOdds, activeTab, priceOdds, overOdds, underOdds]);
    
    // Handle card expand/collapse (for View Less)
    const handleCardToggle = useCallback((cardId) => {
        setExpandedCardIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(cardId)) {
                newSet.delete(cardId);
            } else {
                newSet.add(cardId);
            }
            return newSet;
        });
    }, []);

    const handleToggleSaved = useCallback((snipedOdd) => {
        const cartBet = buildCartBetFromSnipedOdd(snipedOdd);
        const betId = betCartService.generateBetId(cartBet);

        if (!betId) return;

        if (betCartService.isBetSaved(betId)) {
            betCartService.removeBet(betId);
            return;
        }

        betCartService.addBet(cartBet);
    }, [betCartService]);
    
    // Collapse all cards when tab/sport/prop changes
    useEffect(() => {
        setExpandedCardIds(new Set());
    }, [activeTab, selectedSport, selectedPropType]);

    // Set up scroll listener
    useEffect(() => {
        const feedElement = feedRef.current;
        if (feedElement) {
            feedElement.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (feedElement) {
                feedElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);

    // Fetch odds when filters, page, or active tab changes
    useEffect(() => {
        fetchSnipedOdds();
    }, [fetchSnipedOdds]);
    
    // Save view state on scroll position change
    useEffect(() => {
        debouncedSaveViewState();
    }, [scrollPosition, debouncedSaveViewState]);

    useEffect(() => {
        if (isAuthenticated || hasShownScrollSignupPrompt || !feedRef.current || !firstLockedCardRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                AnalyticsService.getInstance().track(EVENT_REGISTRATION_MODAL_SHOWN, { trigger: 'sniped_scroll_gate' });
                setShowRegistrationModal(true);
                setHasShownScrollSignupPrompt(true);
            },
            {
                root: feedRef.current,
                threshold: 0.25
            }
        );

        observer.observe(firstLockedCardRef.current);

        return () => observer.disconnect();
    }, [activeTab, priceOdds, overOdds, underOdds, isAuthenticated, hasShownScrollSignupPrompt]);

    return (
        <>
            <PageView
                header={!isAuthenticated || !canUseSnipedOddsFilters ? null : (
                    <div className={componentStyles.compactFilterHeader}>
                        <div className={componentStyles.activeFilterPills} aria-label="Active sniped odds filters">
                            {visibleFilterPills.map((pill) => (
                                <span
                                    key={pill.key}
                                    className={componentStyles.activeFilterPill}
                                >
                                    {pill.label}
                                </span>
                            ))}
                        </div>
                        <button
                            type="button"
                            className={componentStyles.filtersButton}
                            onClick={openFilterModal}
                            title="Sniped Odds Filters"
                        >
                            <FaFilter />
                            <span>Filters</span>
                            <span
                                className={`${componentStyles.activeFilterCount} ${activeFilterCount === 0 ? componentStyles.activeFilterCountEmpty : ''}`}
                                aria-hidden={activeFilterCount === 0}
                            >
                                {activeFilterCount > 0 ? activeFilterCount : 0}
                            </span>
                        </button>
                    </div>
                )}
                containerClassName={componentStyles.snipedOddsContainer}
                headerClassName={componentStyles.snipedOddsHeader}
                contentClassName={componentStyles.snipedOddsFeed}
            >
                <SnipedOddsSettingsModal
                    isOpen={showFilterModal}
                    onClose={closeFilterModal}
                >
                    <div className={componentStyles.modalConfigContent}>
                        <section className={componentStyles.filterSection}>
                            <div className={componentStyles.filterSectionHeader}>
                                <span>View Type</span>
                                <span>{getCurrentViewLabel(draftActiveTab)}</span>
                            </div>
                            <div className={componentStyles.modalPillsContainer}>
                                <button
                                    type="button"
                                    className={`${componentStyles.modalPill} ${draftActiveTab === 'overs' ? componentStyles.modalPillActive : ''}`}
                                    onClick={() => setDraftActiveTab('overs')}
                                >
                                    <FaArrowUp className={componentStyles.pillIcon} />
                                    Best Overs
                                    <span className={componentStyles.propTypeCount}>
                                        {overCount > 0 ? overCount : overOdds.length}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className={`${componentStyles.modalPill} ${draftActiveTab === 'unders' ? componentStyles.modalPillActive : ''}`}
                                    onClick={() => setDraftActiveTab('unders')}
                                >
                                    <FaArrowDown className={componentStyles.pillIcon} />
                                    Best Unders
                                    <span className={componentStyles.propTypeCount}>
                                        {underCount > 0 ? underCount : underOdds.length}
                                    </span>
                                </button>
                            </div>
                        </section>

                        <section className={componentStyles.filterSection}>
                            <div className={componentStyles.filterSectionHeader}>
                                <span>Sports</span>
                                <span>{draftSelectedSport || 'All Sports'}</span>
                            </div>
                            <div className={componentStyles.modalPillsContainer}>
                                <button
                                    type="button"
                                    className={`${componentStyles.modalPill} ${!draftSelectedSport ? componentStyles.modalPillActive : ''}`}
                                    onClick={() => handleDraftSportSelect(draftSelectedSport)}
                                >
                                    All Sports
                                </button>
                                {supportedSports.map((sportOption) => (
                                    <button
                                        key={sportOption.sportType}
                                        type="button"
                                        className={`${componentStyles.modalPill} ${draftSelectedSport === sportOption.sportType ? componentStyles.modalPillActive : ''}`}
                                        onClick={() => handleDraftSportSelect(sportOption.sportType)}
                                    >
                                        {sportOption.sportType}
                                        {sportOption.count !== null && sportOption.count !== undefined && (
                                            <span className={componentStyles.propTypeCount}>{sportOption.count}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </section>

                        <section className={componentStyles.filterSection}>
                            <div className={componentStyles.filterSectionHeader}>
                                <span>Prop Type</span>
                                <span>{draftSelectedPropType ? getDraftPropDisplayName() : 'All Props'}</span>
                            </div>
                            {!draftSelectedSport ? (
                                <div className={componentStyles.filterHint}>
                                    Select a sport to refine by prop type.
                                </div>
                            ) : loadingDraftProps ? (
                                <div className={componentStyles.filterHint}>
                                    Loading prop types...
                                </div>
                            ) : draftAvailableProps.filter((prop) => isPropAvailable(prop, draftActiveTab)).length === 0 ? (
                                <div className={componentStyles.filterHint}>
                                    No prop types available for {draftSelectedSport}.
                                </div>
                            ) : (
                                <div className={componentStyles.modalPillsContainer}>
                                    <button
                                        type="button"
                                        className={`${componentStyles.modalPill} ${!draftSelectedPropType ? componentStyles.modalPillActive : ''}`}
                                        onClick={() => handleDraftPropSelect(draftSelectedPropType)}
                                    >
                                        All Props
                                    </button>
                                    {draftAvailableProps.filter((prop) => isPropAvailable(prop, draftActiveTab)).map((prop) => {
                                        const propKey = getPropKey(prop);
                                        const propCount = getPropCount(prop, draftActiveTab);
                                        if (!propKey) return null;
                                        return (
                                            <button
                                                key={propKey}
                                                type="button"
                                                className={`${componentStyles.modalPill} ${draftSelectedPropType === propKey ? componentStyles.modalPillActive : ''}`}
                                                onClick={() => handleDraftPropSelect(propKey)}
                                            >
                                                {getPropLabel(prop)}
                                                {propCount !== null && propCount !== undefined && (
                                                    <span className={componentStyles.propTypeCount}>{propCount}</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    </div>
                    <div className={componentStyles.filterModalFooter}>
                        <button
                            type="button"
                            className={globalStyles.secondaryButton}
                            onClick={clearDraftFilters}
                            disabled={draftFilterCount === 0}
                        >
                            <FaTimes />
                            <span>Clear</span>
                        </button>
                        <button
                            type="button"
                            className={globalStyles.primaryButton}
                            onClick={applyDraftFilters}
                        >
                            Apply
                        </button>
                    </div>
                </SnipedOddsSettingsModal>

                {/* Odds Feed */}
            <div 
                ref={feedRef}
                style={{
                    overflowY: 'auto',
                    height: '100%',
                    maxHeight: '100vh'
                }}
            >
                {(() => {
                    // Tab is the only required selector for loading the list.
                    if (!activeTab) {
                        return (
                            <div className={globalStyles.emptyState}>
                                <p>Select Best Overs or Best Unders to view sniped odds</p>
                            </div>
                        );
                    }
                    
                    const currentList = getCurrentList();
                    const displayedItems = injectPromotionalCards(currentList);
                    if (currentList.length === 0 && !loading && !loadingAllOpportunities) {
                        return (
                    <div className={globalStyles.emptyState}>
                                <p>No sniped odds found for this selection.</p>
                    </div>
                        );
                    }
                    return displayedItems.map((item, index) => {
                        if (item.type === 'ad') {
                            return (
                                <AdCard
                                    key={item.indexId}
                                    adId={item.indexId}
                                    title="StatSpyder Pro"
                                    description="Unlock unlimited sniped odds with no ads."
                                    ctaText="Upgrade Now"
                                    sponsoredLabel={true}
                                />
                            );
                        }

                        if (item.type === 'registration') {
                            return <RegistrationSuggestionCard key={item.indexId} />;
                        }

                        const odd = item;
                        // Prefer stable identifiers from the API when available.
                        const stableId = odd.statspyder_document_id || odd._id || odd.indexId;
                        const fallbackCompositeId = `${odd.player_name}_${odd.event_id}_${odd.market_key}_${odd.direction}`;
                        const cardId = stableId || fallbackCompositeId;
                        const isExpanded = expandedCardIds.has(cardId);
                        const isBlurred = shouldBlurCard(displayedItems, item, index);
                        const cartBet = buildCartBetFromSnipedOdd(odd);
                        const cartBetId = betCartService.generateBetId(cartBet);
                        
                        return (
                            <div
                                key={cardId}
                                ref={isFirstLockedCard(displayedItems, item, index) ? firstLockedCardRef : null}
                                className={isBlurred ? componentStyles.blurredCard : ''}
                                style={isBlurred ? { filter: 'blur(4px)', pointerEvents: 'none' } : {}}
                            >
                                <SnipedOddsCard 
                                    snipedOdd={odd}
                                    useContextConfidence={activeTab === 'overs' || activeTab === 'unders'}
                                    isExpanded={isExpanded}
                                    onToggle={() => handleCardToggle(cardId)}
                                    onViewMore={() => handleViewMore(cardId)}
                                    loadingAllOpportunities={loadingAllOpportunities}
                                    isAuthenticated={isAuthenticated}
                                    isSaved={savedBetIds.has(cartBetId)}
                                    onToggleSaved={() => handleToggleSaved(odd)}
                                />
                            </div>
                        );
                    });
                })()}
                {loading && <SmallLoadingAlt />}
            </div>
            </PageView>
            <RegistrationModal
                isOpen={showRegistrationModal}
                onClose={() => setShowRegistrationModal(false)}
                title="View Sniped Odds"
                suggestionProps={{
                    title: 'Create Account',
                    subtitle: 'Sign up or log in to view live sniped odds, player prop edges, and sportsbook line advantages.',
                    primaryLabel: 'Sign Up Now',
                    primaryView: 'register',
                    secondaryLabel: 'Already have an account? Login',
                    secondaryView: 'login',
                    ctaContext: 'sniped_odds_signup'
                }}
            />
            <RegistrationModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title="Upgrade to Pro"
                suggestionProps={{
                    title: 'Unlock Sniped Odds',
                    subtitle: 'Upgrade to Pro to view more sportsbook opportunities and filter sniped odds by sport, prop, and edge.',
                    primaryLabel: 'View Pro Options',
                    primaryView: 'profile',
                    secondaryLabel: '',
                    secondaryView: '',
                    ctaContext: 'sniped_odds_upgrade'
                }}
            />
        </>
    );
};

export default SnipedOdds;
